import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPost, getComments, createComment, generateSummary } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import VoteRail from '../components/VoteRail';
import CommentTree from '../components/CommentTree';
import AISummary from '../components/AISummary';
import ReportButton from '../components/ReportButton';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { votePost } from '../lib/api';
import { formatDate, ROLE_LABELS } from '../lib/utils';
import { Bot, User, MessageSquare, Sparkles, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

export default function PostPage() {
  const { postId } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  useEffect(() => {
    loadPost();
  }, [postId]);

  const loadPost = async () => {
    setLoading(true);
    try {
      const [postRes, commentsRes] = await Promise.all([
        getPost(postId),
        getComments(postId),
      ]);
      setPost(postRes.data);
      setComments(commentsRes.data.comments || []);
    } catch (err) {
      console.error('Error loading post:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (direction) => {
    try {
      const res = await votePost(postId, direction);
      setPost(res.data);
    } catch (err) {
      // Silent
    }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      await createComment(postId, { body: commentText });
      setCommentText('');
      // Reload comments
      const res = await getComments(postId);
      setComments(res.data.comments || []);
      toast.success('Commentaire publié');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const res = await generateSummary(postId);
      setPost(prev => ({ ...prev, summary: res.data }));
      toast.success('Résumé généré !');
    } catch (err) {
      toast.error('Erreur lors de la génération du résumé');
    } finally {
      setGeneratingSummary(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-3 sm:px-4 lg:px-6 py-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="mx-auto max-w-4xl px-3 sm:px-4 lg:px-6 py-12 text-center">
        <h2 className="font-heading text-xl font-bold">Post non trouvé</h2>
        <Link to="/" className="text-sm text-[hsl(var(--primary))] mt-2 inline-block">Retour à l'accueil</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
      {/* Back link */}
      <Link
        to={post.community_slug ? `/s/${post.community_slug}` : '/'}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors duration-150"
      >
        <ArrowLeft className="w-4 h-4" />
        {post.community_slug ? `s@${post.community_slug}` : 'Accueil'}
      </Link>

      {/* Post */}
      <Card className="p-0 overflow-hidden mb-6" data-testid="post-detail">
        <div className="grid grid-cols-[48px_minmax(0,1fr)]">
          {/* Vote Rail */}
          <div className="flex flex-col items-center py-4 bg-[hsl(var(--muted))]/30">
            <VoteRail score={post.score || 0} onVote={handleVote} />
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            {/* Meta */}
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <Link
                to={`/s/${post.community_slug}`}
                className="text-xs font-semibold text-[hsl(var(--primary))] hover:underline"
              >
                s@{post.community_slug}
              </Link>
              <span className="text-xs text-muted-foreground">•</span>
              {post.author_is_ai ? (
                <span className="ai-badge">
                  {post.author_avatar ? (
                    <img src={post.author_avatar} alt="" className="w-4 h-4 rounded-full" />
                  ) : (
                    <Bot className="w-3 h-3" />
                  )}
                  {post.author_name}
                </span>
              ) : (
                <span className="text-xs flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {post.author_name}
                </span>
              )}
              {post.author_role && (
                <Badge variant="outline" className="text-[10px] py-0 h-4">
                  {ROLE_LABELS[post.author_role] || post.author_role}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">{formatDate(post.created_at)}</span>
              {post.flair && <span className="flair-badge">{post.flair}</span>}
            </div>

            {/* Title */}
            <h1 className="font-heading text-xl sm:text-2xl font-bold mb-4">{post.title}</h1>

            {/* Body */}
            <div className="prose-medical text-sm leading-relaxed">
              <ReactMarkdown>{post.body}</ReactMarkdown>
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-4">
                {post.tags.map((tag, i) => (
                  <Badge key={i} variant="outline" className="text-[10px]">{tag}</Badge>
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center gap-3 mt-4 pt-3 border-t">
              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                <MessageSquare className="w-4 h-4" />
                {post.comment_count || 0} commentaires
              </span>
              {comments.length >= 2 && !post.summary && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerateSummary}
                  disabled={generatingSummary}
                  className="text-xs gap-1"
                >
                  {generatingSummary ? (
                    <><Loader2 className="w-3 h-3 animate-spin" /> Génération...</>
                  ) : (
                    <><Sparkles className="w-3 h-3" /> Générer un résumé IA</>
                  )}
                </Button>
              )}
              <ReportButton targetType="post" targetId={post._id} />
            </div>
          </div>
        </div>
      </Card>

      {/* AI Summary */}
      {post.summary && (
        <div className="mb-6">
          <AISummary summary={post.summary} />
        </div>
      )}

      {/* Comment Form */}
      {user && (
        <Card className="p-4 mb-6" data-testid="comment-form">
          <h3 className="font-heading font-semibold text-sm mb-2">Ajouter un commentaire</h3>
          <Textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Votre commentaire..."
            className="min-h-[100px] mb-2"
            data-testid="comment-textarea"
          />
          <Button
            onClick={handleComment}
            disabled={submitting || !commentText.trim()}
            size="sm"
            data-testid="submit-comment-button"
          >
            {submitting ? 'Envoi...' : 'Publier'}
          </Button>
        </Card>
      )}

      {/* Comments */}
      <div data-testid="comments-section">
        <h3 className="font-heading font-semibold mb-4">
          {comments.length} commentaire{comments.length !== 1 ? 's' : ''}
        </h3>
        {comments.length > 0 ? (
          <CommentTree comments={comments} postId={postId} />
        ) : (
          <Card className="p-6 text-center">
            <MessageSquare className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">Aucun commentaire pour le moment</p>
          </Card>
        )}
      </div>
    </div>
  );
}
